"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ServerSideUnitsTree = exports.NotFoundEx = exports.RedirectEx = exports.ServerSideUnitRef = exports.depsOf = exports.ServerSideUnit = void 0;
const fdr_lib_trace_tracing_api_1 = require("@hotelplan/fdr.lib.trace.tracing-api");
function getDisplayName(fn) {
    return fn['displayName'] || fn.name || `unknown`;
}
class ServerSideUnit {
    constructor(deps, value, refOrName = getDisplayName(value)) {
        this.deps = deps;
        this.value = value;
        this.ref =
            typeof refOrName === 'string'
                ? new ServerSideUnitRef(refOrName)
                : refOrName;
    }
    withRef(ref) {
        return new ServerSideUnit(this.deps.slice(0), this.value, ref);
    }
    withDeps(deps) {
        return new ServerSideUnit(this.deps.concat(deps.map(d => (d instanceof ServerSideUnit ? d.ref : d))), this.value, this.ref);
    }
    /**
     * @deprecated Use `withDeps` because it does create a new instance
     * @param deps
     */
    addDeps(deps) {
        return this.withDeps(deps);
    }
    withDep(dep) {
        const d = dep instanceof ServerSideUnit ? dep.ref : dep;
        return new ServerSideUnit(this.deps.concat([d]), this.value, this.ref);
    }
    /**
     * @deprecated Use `withDep` because it does create a new instance
     * @param dep
     */
    addDep(dep) {
        return this.withDep(dep);
    }
    static depsOf(deps) {
        return fn => ServerSideUnit.createServerSideUnit((deps.length ? deps : null), fn);
    }
    static createServerSideUnit(deps, fn, ref) {
        return new ServerSideUnit(deps || [], fn, ref);
    }
    toString() {
        return `Unit#${this.ref.id}(${this.deps.map(dep => dep.id).join(',')})`;
    }
}
exports.ServerSideUnit = ServerSideUnit;
const { depsOf } = ServerSideUnit;
exports.depsOf = depsOf;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
class ServerSideUnitRef {
    constructor(idOrSymbol) {
        this.id = 'unknown';
        this.symbol = Symbol(this.id);
        if (typeof idOrSymbol === 'string') {
            this.id = idOrSymbol;
            this.symbol = Symbol(this.id);
        }
        else if (typeof idOrSymbol === 'symbol') {
            this.symbol = idOrSymbol;
            this.id = this.symbol.description;
        }
    }
}
exports.ServerSideUnitRef = ServerSideUnitRef;
class RedirectEx {
    constructor(redirect) {
        this.redirect = redirect;
    }
}
exports.RedirectEx = RedirectEx;
class NotFoundEx {
    constructor(message) {
        this.message = message;
    }
}
exports.NotFoundEx = NotFoundEx;
class ServerSideUnitsTree {
    constructor(units, initialProps = {}) {
        this.units = units;
        this.initialProps = initialProps;
        this.entries = [];
        this.bySymbol = new Map();
        this.bySymbolDegree = new Map();
        this.bySymbolChildren = new Map();
        units.forEach(u => {
            var _a, _b, _c;
            if (process.env.NODE_ENV === 'development') {
                if (this.bySymbol.has(u.ref.symbol)) {
                    // eslint-disable-next-line no-console
                    console.warn(`There is a duplicate unit for ${u.ref.id}`);
                }
            }
            this.bySymbol.set(u.ref.symbol, u);
            this.bySymbolDegree.set(u.ref.symbol, ((_a = u.deps) === null || _a === void 0 ? void 0 : _a.length) || 0);
            if (!((_b = u.deps) === null || _b === void 0 ? void 0 : _b.length)) {
                this.entries.push(u);
            }
            (_c = u.deps) === null || _c === void 0 ? void 0 : _c.forEach(d => {
                if (!this.bySymbolChildren.has(d.symbol)) {
                    this.bySymbolChildren.set(d.symbol, [u]);
                }
                else {
                    this.bySymbolChildren.get(d.symbol).push(u);
                }
            });
        });
        units.forEach(u => {
            var _a;
            (_a = u.deps) === null || _a === void 0 ? void 0 : _a.forEach(d => {
                const found = this.bySymbol.get(d.symbol);
                if (!found) {
                    throw `[FDR] Could not find unit by reference '${d.id}' for '${u.ref.id}'`;
                }
            });
        });
        this.run = (0, fdr_lib_trace_tracing_api_1.traceFn)(`SSP Units`)(this.run.bind(this));
    }
    run(ctx) {
        return new Promise((resolveOriginal, reject) => {
            const resultMap = new Map();
            const executionDegree = new Map(this.bySymbolDegree);
            let allDegree = this.units.length;
            let stop = false;
            const props = Object.assign({}, this.initialProps);
            const resolve = (finishResult) => {
                stop = true;
                resolveOriginal(finishResult);
            };
            const propsContainers = [];
            const execute = ({ deps, value: unitFn, ref: { id, symbol }, }) => __awaiter(this, void 0, void 0, function* () {
                if (stop)
                    return;
                const results = deps.map(d => resultMap.get(d.symbol)) || [];
                (0, fdr_lib_trace_tracing_api_1.traceFn)(`Unit ${id}`)(unitFn)(ctx, ...results)
                    .then(result => {
                    if (stop)
                        return;
                    resultMap.set(symbol, result);
                    if ('redirect' in result) {
                        resolve({ redirect: result.redirect });
                        return;
                    }
                    else if ('notFound' in result && result.notFound) {
                        resolve({ notFound: true });
                        return;
                    }
                    else if ('props' in result) {
                        propsContainers.push(result.props);
                    }
                    const children = this.bySymbolChildren.get(symbol);
                    children === null || children === void 0 ? void 0 : children.forEach(child => {
                        const nextDegree = (executionDegree.get(child.ref.symbol) || 1) - 1;
                        executionDegree.set(child.ref.symbol, nextDegree);
                        if (!nextDegree)
                            execute(child);
                    });
                    allDegree--;
                    if (allDegree === 0) {
                        propsContainers.forEach(p => Object.assign(props, p));
                        resolve({ props });
                    }
                })
                    .catch(e => {
                    if (e instanceof RedirectEx) {
                        return resolve({ redirect: e.redirect });
                    }
                    if (e instanceof NotFoundEx) {
                        return resolve({ notFound: true });
                    }
                    stop = true;
                    reject(e);
                });
            });
            this.entries.forEach(execute);
        });
    }
}
exports.ServerSideUnitsTree = ServerSideUnitsTree;
//# sourceMappingURL=index.js.map